export const CONVERSATION_PHONE_CALLS_FLAG = 'conversation_phone_calls';
export const CONVERSATION_VIDEO_CALLS_FLAG = 'conversation_video_calls';
export const CONVERSATION_PHONE_CALLS_ADMIN_ONLY_FLAG =
  'conversation_phone_calls.admin_only';
export const CONVERSATION_VIDEO_CALLS_ADMIN_ONLY_FLAG =
  'conversation_video_calls.admin_only';

export const CONVERSATION_CALL_MESSAGE_TYPE = 'conversation_call';

export const CONVERSATION_CALL_KINDS = ['phone', 'video'] as const;
export type ConversationCallKind = (typeof CONVERSATION_CALL_KINDS)[number];

export const CONVERSATION_CALL_STATUSES = [
  'connecting',
  'in_progress',
  'completed',
  'missed',
  'canceled',
  'failed'
] as const;
export type ConversationCallStatus =
  (typeof CONVERSATION_CALL_STATUSES)[number];

export const TERMINAL_CONVERSATION_CALL_STATUSES: readonly ConversationCallStatus[] =
  ['completed', 'missed', 'canceled', 'failed'];

export const isTerminalCallStatus = (status: ConversationCallStatus): boolean =>
  TERMINAL_CONVERSATION_CALL_STATUSES.includes(status);

export const CONVERSATION_CALL_ORIGINS = ['web', 'app', 'inbound'] as const;
export type ConversationCallOrigin = (typeof CONVERSATION_CALL_ORIGINS)[number];

export const VIDEO_CALL_JOIN_WINDOW_MINUTES = 60;

export const CALL_REFUSAL_MESSAGES = {
  feature_disabled: "Calls aren't available yet.",
  unsupported_conversation:
    'Calls are only available between a buyer and a breeder.',
  blocked: "You can't call in this conversation.",
  unavailable: "This person can't take calls right now.",
  awaiting_reply: "You can call once they've written to you here.",
  caller_phone_unverified: 'Verify your phone number to make calls.',
  phone_not_callable:
    'Calls work with Canadian and US phone numbers. Add one to your profile and verify it to make calls.',
  callee_phone_unavailable:
    "They haven't verified a phone number yet. Send a message instead.",
  quiet_hours: "It's late where they are. Calls open again at 8 am their time.",
  call_in_progress: 'A call in this conversation is already under way.',
  app_required: 'Start video calls from the Paction app.',
  call_ended: 'This call has ended.',
  too_many_calls:
    "You've started several calls in a row. Please wait a minute and try again.",
  not_configured: 'Calling is temporarily unavailable. Please try again later.'
} as const;

export type CallRefusalCode = keyof typeof CALL_REFUSAL_MESSAGES;

export const isCallRefusalCode = (value: unknown): value is CallRefusalCode =>
  typeof value === 'string' && value in CALL_REFUSAL_MESSAGES;

export type CallAvailability =
  | { available: true }
  | { available: false; reason: CallRefusalCode; message: string };

export type ConversationCallAvailability = {
  phone: CallAvailability;
  video: CallAvailability;
};

export type ConversationCallCard = {
  callId: number;
  kind: ConversationCallKind;
  status: ConversationCallStatus;
  callerUserId: number | null;
  durationSeconds: number | null;
  joinableUntil: string | null;
  callerConnected?: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const optionalNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export const parseConversationCallCard = (
  systemMessageType: string | null | undefined,
  data: unknown
): ConversationCallCard | null => {
  if (systemMessageType !== CONVERSATION_CALL_MESSAGE_TYPE) return null;
  if (!isRecord(data)) return null;
  const callId = optionalNumber(data.callId);
  const kind = data.kind;
  const status = data.status;
  if (
    callId === null ||
    !(CONVERSATION_CALL_KINDS as readonly unknown[]).includes(kind) ||
    !(CONVERSATION_CALL_STATUSES as readonly unknown[]).includes(status)
  ) {
    return null;
  }
  return {
    callId,
    kind: kind as ConversationCallKind,
    status: status as ConversationCallStatus,
    callerUserId: optionalNumber(data.callerUserId),
    durationSeconds: optionalNumber(data.durationSeconds),
    joinableUntil:
      typeof data.joinableUntil === 'string' ? data.joinableUntil : null,
    callerConnected: data.callerConnected === true
  };
};

export const PHONE_CALL_STEPS = {
  callerAnswer: 'Answer the call from Paction, then press any key',
  ringingCallee: 'Ringing them now. Stay on the line',
  calleeWaiting: 'Paction will ring your phone in a moment',
  calleeRinging: 'Your phone is ringing. Answer it and press any key'
} as const;

export const formatCallDuration = (totalSeconds: number): string => {
  const seconds = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = String(seconds % 60).padStart(2, '0');
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${remainder}`
    : `${minutes}:${remainder}`;
};

export const isVideoCallJoinable = (
  card: ConversationCallCard,
  now: Date = new Date()
): boolean =>
  card.kind === 'video' &&
  !isTerminalCallStatus(card.status) &&
  card.joinableUntil !== null &&
  new Date(card.joinableUntil).getTime() > now.getTime();

export type ConversationCallDescription = {
  title: string;
  detail: string | null;
};

export const describeConversationCall = (
  card: ConversationCallCard,
  viewerUserId: number | null,
  now: Date = new Date()
): ConversationCallDescription => {
  const viewerStarted =
    viewerUserId !== null && card.callerUserId === viewerUserId;
  const noun = card.kind === 'video' ? 'Video call' : 'Phone call';
  const duration =
    card.durationSeconds !== null
      ? formatCallDuration(card.durationSeconds)
      : null;

  switch (card.status) {
    case 'connecting':
      if (card.kind === 'video') {
        if (!isVideoCallJoinable(card, now)) {
          return { title: noun, detail: 'This call has ended' };
        }
        return {
          title: noun,
          detail: viewerStarted
            ? 'Waiting for them to join'
            : 'Tap Join to start the call'
        };
      }
      if (viewerStarted) {
        return {
          title: noun,
          detail: card.callerConnected
            ? PHONE_CALL_STEPS.ringingCallee
            : PHONE_CALL_STEPS.callerAnswer
        };
      }
      return {
        title: noun,
        detail: card.callerConnected
          ? PHONE_CALL_STEPS.calleeRinging
          : PHONE_CALL_STEPS.calleeWaiting
      };
    case 'in_progress':
      return { title: `${noun} in progress`, detail: null };
    case 'completed':
      return { title: noun, detail: duration };
    case 'missed':
      return {
        title: viewerStarted
          ? `${noun} not answered`
          : `Missed ${noun.toLowerCase()}`,
        detail: null
      };
    case 'canceled':
      return { title: `${noun} canceled`, detail: null };
    case 'failed':
      return { title: `${noun} didn't connect`, detail: null };
  }
};

export const callCardPreview = (card: ConversationCallCard): string =>
  describeConversationCall(card, null).title;
